import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Crown } from "lucide-react";
import { PLAN_TIERS } from "@/lib/plans";
import { useState } from "react";

export default function PricingPage() {
  const { user, quota } = useAuth();
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);

  const subscribe = async (lookup_key) => {
    if (!user) {
      navigate("/inscription");
      return;
    }
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-28 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="pricing-main">
        <div className="text-center max-w-2xl mx-auto mb-16 animate-fade-up">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-400">Nos forfaits</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold mt-3">Créez au rythme qui vous convient</h1>
          <p className="text-gray-400 mt-4">Sans engagement. Résiliez ou changez de plan à tout moment.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {PLAN_TIERS.map((tier) => {
            const current = quota?.plan === tier.key;
            return (
              <div
                key={tier.key}
                className={`relative rounded-2xl p-8 border card-hover ${tier.popular ? 'border-indigo-500/40 bg-gradient-to-b from-indigo-500/10 to-transparent' : 'glass'}`}
                data-testid={`plan-${tier.key}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-indigo-500 text-white text-xs font-semibold uppercase tracking-wider">
                    Le plus choisi
                  </div>
                )}
                {tier.key === "premium" && (
                  <Crown size={16} className="absolute top-6 right-6 text-amber-400" />
                )}
                <div className="text-sm font-mono uppercase tracking-widest text-gray-400">{tier.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold">{tier.price}</span>
                  <span className="text-gray-500">€/mois</span>
                </div>
                <div className="mt-2 text-sm text-indigo-300">{tier.quota}</div>
                <ul className="mt-6 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(tier.lookup_key)}
                  disabled={current || loadingKey === tier.lookup_key}
                  className={`mt-8 w-full ${tier.popular ? 'pill-primary' : 'pill-ghost'} disabled:opacity-50`}
                  data-testid={`subscribe-${tier.key}`}
                >
                  {current ? "Forfait actuel" : loadingKey === tier.lookup_key ? "Redirection…" : tier.cta}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center text-sm text-gray-500">
          Paiement sécurisé par Stripe. Cartes de test acceptées : 4242 4242 4242 4242 (test uniquement).
        </div>
      </main>
    </div>
  );
}
